/**
 * ATHENA SPEECH CLEANER
 * Cleans raw STT output before sending to the AI.
 * Pure local heuristics — no API calls, no network, zero latency impact.
 */

// Matches "Athena" and "Atena" variants at the very start (wake-word removal).
// [Hh]? makes the 'h' optional so both spellings are covered.
const LEADING_WAKE_RE = /^(\s*[Aa][Tt][Hh]?[Ee][Nn]?[Aa]\s*)+/;

// ── Correção 06: Semantic Speech Reconstruction ──────────────────────────────

// Pure hesitation sounds with zero semantic value in pt-BR STT output.
// No \b: accented chars (é, â) are not \w in JS regex, so \b fails for them.
// Pattern is safe for STT context where hesitations appear as standalone tokens.
const HESITATION_RE = /(?:[eé]h+|ah+|[mh]{2,}|[âê]h?|oh{2,}|hein+)\s*/gi;

// Sentence-final fillers that add no meaning ("né?", "sabe?", "entendeu?")
// Only removed when they appear at the END of the phrase
const FINAL_FILLER_RE = /[\s,]+(?:sabe|né|entendeu|certo|tá bom|ta bom|okay|ok)[?!]?\s*$/i;

// Self-correction pattern: "word... word" where speaker corrects mid-phrase
// Matches "quer dizer" / "ou seja" pivots and discards the pre-pivot fragment
const CORRECTION_PIVOT_RE = /^.{1,40}?\b(?:quer dizer|ou seja|na verdade|isto é|isso é)\s+/i;

// --- Helpers ----------------------------------------------------------------

function detectCorruption(text) {
  const words = text.trim().toLowerCase().split(/\s+/);

  // Too many wake-word occurrences
  const wakeCount = words.filter(w => /^a[t]h?e[n]?a$/.test(w)).length;
  if (wakeCount > 3) return true;

  // Single word repeated more than 4 times
  const freq = {};
  for (const w of words) {
    freq[w] = (freq[w] || 0) + 1;
    if (freq[w] > 4) return true;
  }

  // Very low word diversity (< 30% unique words)
  const unique = new Set(words);
  if (words.length > 6 && unique.size / words.length < 0.3) return true;

  // Almost entirely wake words
  const withoutWake = text.replace(/a[t]h?e[n]?a/gi, '').trim();
  if (withoutWake.length < 3) return true;

  return false;
}

/** Remove hesitation sounds, final fillers, and self-correction pivots. */
function removeSpeechNoise(text) {
  let t = text;
  // Handle self-correction pivots first ("eu queria... quer dizer, eu preciso")
  const pivotMatch = t.match(CORRECTION_PIVOT_RE);
  if (pivotMatch) t = t.slice(pivotMatch[0].length).trim();
  // Remove hesitation sounds (pure audio noise)
  t = t.replace(HESITATION_RE, ' ');
  // Remove meaningless fillers at end of phrase
  t = t.replace(FINAL_FILLER_RE, '');
  return t.replace(/\s+/g, ' ').trim();
}

/** Add terminal punctuation if the phrase ends without any. */
function addTerminalPunctuation(text) {
  if (!text) return text;
  return /[.!?…]$/.test(text) ? text : text + '.';
}

/**
 * Remove Athena/Atena from the beginning only (activation wake word).
 * Preserves "Athena" when it appears as a meaningful subject mid-sentence.
 */
function removeLeadingWakeWords(text) {
  const stripped = text.replace(LEADING_WAKE_RE, '').trim();
  // Don't strip if nothing meaningful remains
  return stripped.length >= 2 ? stripped : text;
}

/**
 * Remove consecutive duplicate words/phrases.
 * Phase 1: multi-word n-gram dedup via regex (n=2..6).
 * Phase 2: single-word dedup via word-array comparison (Unicode-safe).
 */
function removeConsecutiveDuplicates(text) {
  let t = text;

  // Multi-word phrase dedup (regex safe for n≥2)
  for (let n = 2; n <= 6; n++) {
    const pat = Array(n).fill('[^\\s]+').join('\\s+');
    t = t.replace(new RegExp(`(${pat})(\\s+\\1)+`, 'gi'), '$1');
  }

  // Single-word dedup: explicit word comparison (avoids regex/Unicode quirks)
  const words = t.split(/\s+/);
  const result = [];
  for (let i = 0; i < words.length; i++) {
    if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
      result.push(words[i]);
    }
  }
  return result.join(' ');
}

/** Estimate transcript quality 0.0–1.0. */
function calculateConfidence(raw, clean, isCorrupted) {
  if (isCorrupted) return 0.2;
  if (!clean || clean.length < 3) return 0.1;

  const rawWords   = raw.split(/\s+/).length;
  const cleanWords = clean.split(/\s+/).length;
  const retention  = Math.min(cleanWords / Math.max(rawWords, 1), 1);

  const cw        = clean.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  const diversity = new Set(cw).size / Math.max(cw.length, 1);

  // Loose Portuguese verb/intent detection
  const hasVerb = /\b(quero|queria|posso|pode|fazer|ir|criar|abrir|ver|saber|entender|preciso|tenho|tem|vou|vai|faz|quer|está|é|são|me|você|deixa|abre|mostra|analisa|explica|ajuda|gostaria|consigo|muda|salva)\b/i.test(clean);

  let score = 0.45;
  if (retention  > 0.6)  score += 0.15;
  if (diversity  > 0.65) score += 0.15;
  if (hasVerb)           score += 0.15;
  if (cleanWords >= 3)   score += 0.10;

  return Math.min(Math.round(score * 100) / 100, 1.0);
}

// --- Public API -------------------------------------------------------------

/**
 * Clean a raw STT transcript before sending it to the AI.
 *
 * @param {string} rawText - Raw Speech Recognition output
 * @returns {{
 *   rawTranscript:         string,
 *   cleanTranscript:       string,
 *   confidence:            number,  // 0.0 – 1.0
 *   wasCleaned:            boolean,
 *   isCorrupted:           boolean,
 *   shouldAskConfirmation: boolean
 * }}
 */
export function cleanSpeechTranscript(rawText) {
  const raw = (rawText || '').trim();

  if (!raw) {
    return {
      rawTranscript: raw, cleanTranscript: '', confidence: 0,
      wasCleaned: false, isCorrupted: false, shouldAskConfirmation: false,
    };
  }

  const isCorrupted = detectCorruption(raw);
  let clean = raw;
  let wasCleaned = false;

  // 1 — Remove leading wake words (Athena/Atena)
  const noWake = removeLeadingWakeWords(clean);
  if (noWake !== clean) { clean = noWake; wasCleaned = true; }

  // 2 — Remove consecutive duplicates (multi-word + single-word)
  const deduped = removeConsecutiveDuplicates(clean);
  if (deduped !== clean) { clean = deduped; wasCleaned = true; }

  // 3 — Correção 06: remove hesitation sounds, final fillers, self-corrections
  const noNoise = removeSpeechNoise(clean);
  if (noNoise !== clean) { clean = noNoise; wasCleaned = true; }

  // 4 — Normalize whitespace
  clean = clean.replace(/\s+/g, ' ').trim();

  // 5 — Length cap
  if (clean.length > 400) {
    const cut = clean.lastIndexOf(' ', 400);
    clean = clean.substring(0, cut > 80 ? cut : 400);
    wasCleaned = true;
  }

  // 6 — Capitalize first character
  if (clean) clean = clean.charAt(0).toUpperCase() + clean.slice(1);

  // 7 — Correção 06: add terminal punctuation (makes transcript read as written text)
  clean = addTerminalPunctuation(clean);

  const confidence = calculateConfidence(raw, clean, isCorrupted);

  // Ask for confirmation when corrupted AND still low confidence after cleaning
  const shouldAskConfirmation = isCorrupted && confidence < 0.35;

  if (process.env.NODE_ENV !== 'production' && wasCleaned) {
    console.debug(
      `[SPEECH_CLEANER] raw="${raw.substring(0, 70)}"`,
      `→ clean="${clean.substring(0, 70)}"`,
      `conf=${confidence} corrupted=${isCorrupted}`,
    );
  }

  return {
    rawTranscript: raw,
    cleanTranscript: clean,
    confidence,
    wasCleaned,
    isCorrupted,
    shouldAskConfirmation,
  };
}
